
/**
 * TST Bundle Cart Guard
 * Version: 3.0
 *
 * Main product + linked gifts.
 *
 * Features:
 * - Bundle quantity synchronization.
 * - Remove individual gift.
 * - Persist declined gifts in Shopify cart attributes.
 * - Never restore a deliberately removed gift automatically.
 * - Remove orphan gifts.
 * - Serialized Cart mutations.
 * - Final Cart Drawer rendering.
 *
 * Requires Dawn's cart.js.
 */

(() => {
  'use strict';

  if (window.TSTBundleCartGuard) {
    return;
  }

  const shopRoot = (
    window.Shopify?.routes?.root || '/'
  ).replace(/\/?$/, '/');

  const endpoints = {
    cart: `${shopRoot}cart.js`,
    update: `${shopRoot}cart/update.js`,
    add: `${shopRoot}cart/add.js`
  };

  const state = {
    cart: null,
    queue: Promise.resolve(),
    pending: 0,
    installed: false
  };

  const toQuantity = (value) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return 0;
    }

    return Math.max(0, Math.floor(number));
  };

  const getBundleId = (item) => {
    return String(
      item?.properties?._tst_bundle_id || ''
    );
  };

  const isMain = (item) => {
    return (
      item?.properties?._tst_bundle_role === 'main' &&
      Boolean(getBundleId(item))
    );
  };

  const isGift = (item) => {
    return (
      item?.properties?._tst_bundle_role === 'gift' &&
      Boolean(getBundleId(item))
    );
  };

  /**
   * Decline state is stored in Shopify Cart Attributes.
   *
   * Example:
   *
   * _tst_declined_tst-123-abc = ["456","789"]
   *
   * It survives a page refresh.
   */
  const declineAttributeName = (bundleId) => {
    return `_tst_declined_${bundleId}`;
  };

  function getDeclinedGifts(cart, bundleId) {
    const raw = cart.attributes?.[
      declineAttributeName(bundleId)
    ];

    if (!raw) {
      return new Set();
    }

    try {
      const parsed = JSON.parse(raw);

      if (!Array.isArray(parsed)) {
        return new Set();
      }

      return new Set(
        parsed.map((id) => String(id))
      );
    } catch (error) {
      console.warn(
        '[TST Bundle] Invalid declined gift data',
        error
      );

      return new Set();
    }
  }

  function getDeclinedAttribute(cart, bundleId, variantId) {
    const declined = getDeclinedGifts(
      cart,
      bundleId
    );

    declined.add(String(variantId));

    return {
      [declineAttributeName(bundleId)]:
        JSON.stringify([...declined])
    };
  }

  async function readCart() {
    const response = await fetch(endpoints.cart, {
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(
        `Unable to read cart (${response.status})`
      );
    }

    return response.json();
  }

  async function postCart(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json().catch(
      () => ({})
    );

    if (!response.ok || result.errors) {
      throw new Error(
        result.description ||
        result.message ||
        'Unable to update cart'
      );
    }

    return result;
  }

  function parseGiftRules(item) {
    const raw = item?.properties?._tst_gift_rules;

    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw);

      if (
        !parsed ||
        typeof parsed !== 'object' ||
        Array.isArray(parsed)
      ) {
        return null;
      }

      return parsed;
    } catch (error) {
      console.warn(
        '[TST Bundle] Invalid gift rules',
        error
      );

      return null;
    }
  }

  /**
   * Calculate gift entitlement for one bundle.
   *
   * Supports:
   * - optionGifts
   * - optionGiftsByQuantity
   * - milestones
   */
  function calculateGiftTargets(rules, mainQuantity) {
    const targets = new Map();

    if (!rules || mainQuantity <= 0) {
      return targets;
    }

    let optionEntries = rules.optionGifts || [];

    if (
      Array.isArray(rules.optionGiftsByQuantity)
    ) {
      const availableOptions =
        rules.optionGiftsByQuantity
          .filter((entry) => {
            return (
              Array.isArray(entry) &&
              toQuantity(entry[0]) <= mainQuantity
            );
          })
          .sort((a, b) => {
            return toQuantity(b[0]) - toQuantity(a[0]);
          });

      optionEntries = availableOptions.length
        ? availableOptions[0][1] || []
        : [];
    }

    const optionTargets = new Map();

    for (const entry of optionEntries) {
      if (!Array.isArray(entry)) {
        continue;
      }

      const [variantId, quantity] = entry;
      const id = String(variantId);

      if (
        !toQuantity(variantId) ||
        !toQuantity(quantity)
      ) {
        continue;
      }

      optionTargets.set(
        id,
        Math.max(
          optionTargets.get(id) || 0,
          toQuantity(quantity)
        )
      );
    }

    const milestoneTargets = new Map();

    for (const entry of rules.milestones || []) {
      if (!Array.isArray(entry)) {
        continue;
      }

      const [
        variantId,
        threshold,
        quantity,
        mode
      ] = entry;

      if (
        !toQuantity(variantId) ||
        !toQuantity(quantity)
      ) {
        continue;
      }

      const unlocked =
        mode === 'exact'
          ? mainQuantity === toQuantity(threshold)
          : mainQuantity >= toQuantity(threshold);

      if (!unlocked) {
        continue;
      }

      const id = String(variantId);

      milestoneTargets.set(
        id,
        (milestoneTargets.get(id) || 0) +
          toQuantity(quantity)
      );
    }

    const allVariants = new Set([
      ...optionTargets.keys(),
      ...milestoneTargets.keys()
    ]);

    for (const variantId of allVariants) {
      const target = Math.max(
        optionTargets.get(variantId) || 0,
        milestoneTargets.get(variantId) || 0
      );

      if (target > 0) {
        targets.set(variantId, target);
      }
    }

    return targets;
  }

  /**
   * Build bundle state using the latest Cart.
   */
  function collectBundles(cart, override = null) {
    const bundles = new Map();

    for (const item of cart.items || []) {
      if (!isMain(item)) {
        continue;
      }

      const effectiveQuantity =
        override && item.key === override.key
          ? toQuantity(override.quantity)
          : toQuantity(item.quantity);

      if (effectiveQuantity <= 0) {
        continue;
      }

      const bundleId = getBundleId(item);

      if (!bundles.has(bundleId)) {
        bundles.set(bundleId, {
          quantity: 0,
          rules: null,
          mainVariants: new Set()
        });
      }

      const bundle = bundles.get(bundleId);

      bundle.quantity += effectiveQuantity;

      bundle.mainVariants.add(
        String(item.variant_id)
      );

      if (!bundle.rules) {
        bundle.rules = parseGiftRules(item);
      }
    }

    return bundles;
  }

  /**
   * Compare desired gift state with actual Cart.
   *
   * IMPORTANT:
   * Declined gifts are excluded from all targets.
   */
  function calculateChanges(cart, override = null) {
    const bundles = collectBundles(
      cart,
      override
    );

    const updates = {};
    const additions = [];

    if (override) {
      updates[override.key] = override.quantity;
    }

    const giftsByBundle = new Map();

    for (const item of cart.items || []) {
      if (!isGift(item)) {
        continue;
      }

      const bundleId = getBundleId(item);

      if (!giftsByBundle.has(bundleId)) {
        giftsByBundle.set(
          bundleId,
          new Map()
        );
      }

      const variants = giftsByBundle.get(bundleId);
      const variantId = String(item.variant_id);

      if (!variants.has(variantId)) {
        variants.set(variantId, []);
      }

      variants.get(variantId).push(item);
    }

    const allBundleIds = new Set([
      ...bundles.keys(),
      ...giftsByBundle.keys()
    ]);

    for (const bundleId of allBundleIds) {
      const bundle = bundles.get(bundleId);

      const existing =
        giftsByBundle.get(bundleId) ||
        new Map();

      const desired = bundle?.rules
        ? calculateGiftTargets(
            bundle.rules,
            bundle.quantity
          )
        : new Map();

      /**
       * Exclude variants already purchased as main products.
       */
      if (bundle) {
        for (const variantId of bundle.mainVariants) {
          desired.delete(variantId);
        }
      }

      /**
       * Exclude customer-declined gifts.
       */
      const declined = getDeclinedGifts(
        cart,
        bundleId
      );

      for (const variantId of declined) {
        desired.delete(variantId);
      }

      /**
       * Update or remove existing gift lines.
       */
      for (const [variantId, lines] of existing) {
        let targetQuantity =
          desired.get(variantId) || 0;

        for (const line of lines) {
          if (
            toQuantity(line.quantity) !== targetQuantity
          ) {
            updates[line.key] = targetQuantity;
          }

          targetQuantity = 0;
        }

        desired.delete(variantId);
      }

      /**
       * Add newly unlocked gifts.
       */
      for (const [variantId, giftQuantity] of desired) {
        if (giftQuantity <= 0) {
          continue;
        }

        additions.push({
          id: Number(variantId),
          quantity: giftQuantity,
          properties: {
            _tst_bundle_id: bundleId,
            _tst_bundle_role: 'gift'
          }
        });
      }
    }

    return {
      updates,
      additions,
      changed:
        Object.keys(updates).length > 0 ||
        additions.length > 0
    };
  }

  /**
   * Serialize all controller operations.
   */
  function enqueue(operation) {
    const next = state.queue.then(operation);

    state.queue = next.catch(() => {});

    return next;
  }

  function setBusy() {
    const busy = state.pending > 0;

    document.documentElement.classList.toggle(
      'tst-bundle-updating',
      busy
    );

    document.querySelectorAll(
      '#CartDrawer-Checkout, #checkout, button[name="checkout"]'
    ).forEach((button) => {
      if (busy) {
        if (!button.disabled) {
          button.dataset.tstGuardDisabled = 'true';
        }

        button.disabled = true;

        button.setAttribute(
          'aria-busy',
          'true'
        );
      } else {
        if (
          button.dataset.tstGuardDisabled === 'true'
        ) {
          button.disabled = false;
        }

        delete button.dataset.tstGuardDisabled;

        button.removeAttribute('aria-busy');
      }
    });
  }

  function runExclusive(operation) {
    state.pending += 1;
    setBusy();

    return enqueue(operation).finally(() => {
      state.pending -= 1;
      setBusy();
    });
  }

  function getSectionIds() {
    return [
      'cart-drawer',
      'cart-icon-bubble',
      'main-cart-items',
      'main-cart-footer',
      'cart-live-region-text'
    ];
  }

  async function fetchSections() {
    const ids = getSectionIds();

    const response = await fetch(
      `${shopRoot}?sections=${ids.join(',')}`,
      {
        credentials: 'same-origin',
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to refresh cart sections'
      );
    }

    return response.json();
  }

  /**
   * Update existing DOM from final Shopify sections.
   */
  function renderSections(sections) {
    const parser = new DOMParser();

    const parse = (html) => {
      return parser.parseFromString(
        html,
        'text/html'
      );
    };

    const drawer = document.querySelector(
      'cart-drawer'
    );

    if (
      drawer &&
      sections['cart-drawer']
    ) {
      const source = parse(
        sections['cart-drawer']
      );

      const freshDrawer = source.querySelector(
        'cart-drawer'
      );

      const currentInner = drawer.querySelector(
        '.drawer__inner'
      );

      const freshInner = freshDrawer?.querySelector(
        '.drawer__inner'
      );

      if (currentInner && freshInner) {
        const countdownValues = Array.from(
          currentInner.querySelectorAll(
            '[data-cart-drawer-countdown]'
          )
        ).map((element) => element.textContent);

        currentInner.innerHTML =
          freshInner.innerHTML;

        const countdowns = currentInner.querySelectorAll(
          '[data-cart-drawer-countdown]'
        );

        countdowns.forEach((element, index) => {
          if (countdownValues[index]) {
            element.textContent = countdownValues[index];
          }
        });

        drawer.classList.toggle(
          'is-empty',
          freshDrawer.classList.contains('is-empty')
        );

        currentInner.classList.toggle(
          'is-empty',
          freshInner.classList.contains('is-empty')
        );

        drawer.initEnhancements?.();
      }
    }

    const bubble = document.getElementById(
      'cart-icon-bubble'
    );

    if (
      bubble &&
      sections['cart-icon-bubble']
    ) {
      const next = parse(
        sections['cart-icon-bubble']
      ).getElementById('cart-icon-bubble');

      if (next) {
        bubble.innerHTML = next.innerHTML;
      }
    }

    const cartSection = document.getElementById(
      'main-cart-items'
    );

    if (
      cartSection &&
      sections['main-cart-items']
    ) {
      const next = parse(
        sections['main-cart-items']
      ).getElementById('main-cart-items');

      const currentContents =
        cartSection.querySelector('.js-contents');

      const nextContents =
        next?.querySelector('.js-contents');

      if (currentContents && nextContents) {
        currentContents.innerHTML =
          nextContents.innerHTML;
      }
    }

    const footerSection = document.getElementById(
      'main-cart-footer'
    );

    if (
      footerSection &&
      sections['main-cart-footer']
    ) {
      const next = parse(
        sections['main-cart-footer']
      ).getElementById('main-cart-footer');

      const currentContents =
        footerSection.querySelector('.js-contents');

      const nextContents =
        next?.querySelector('.js-contents');

      if (currentContents && nextContents) {
        currentContents.innerHTML =
          nextContents.innerHTML;
      }
    }
  }

  /**
   * Refresh the final Cart UI.
   */
  async function refreshDrawer(open = false) {
    const sections = await fetchSections();

    renderSections(sections);

    state.cart = await readCart();

    lockCartPageGiftRows(state.cart);

    const drawer = document.querySelector(
      'cart-drawer'
    );

    if (open && drawer) {
      drawer.open?.();
    }

    document.dispatchEvent(
      new CustomEvent('tst:cart-updated', {
        detail: {
          cart: state.cart
        }
      })
    );

    return state.cart;
  }

  /**
   * Lock gift quantities on /cart.
   * Drawer gift quantities are already static in Liquid.
   */
  function lockCartPageGiftRows(cart) {
    if (!document.querySelector('cart-items')) {
      return;
    }

    cart.items.forEach((item, index) => {
      if (!isGift(item)) {
        return;
      }

      const row = document.getElementById(
        `CartItem-${index + 1}`
      );

      if (!row) {
        return;
      }

      row.dataset.tstLinkedGift = 'true';

      row.querySelectorAll(
        'quantity-input, cart-remove-button, .cart-item__quantity-wrapper'
      ).forEach((control) => {
        control.hidden = true;
      });

      let text = row.querySelector(
        '.tst-gift-qty-static'
      );

      if (!text) {
        text = document.createElement('span');
        text.className = 'tst-gift-qty-static';

        const container =
          row.querySelector('.cart-item__quantity') ||
          row;

        container.appendChild(text);
      }

      text.textContent =
        `Qty: ${item.quantity} · Included with bundle`;
    });
  }

  /**
   * Commit changes without rendering intermediate states.
   */
  async function commitChanges(
    changes,
    attributes = null
  ) {
    const hasUpdates =
      Object.keys(changes.updates).length > 0;

    const hasAttributes =
      attributes &&
      Object.keys(attributes).length > 0;

    if (hasUpdates || hasAttributes) {
      const payload = {};

      if (hasUpdates) {
        payload.updates = changes.updates;
      }

      if (hasAttributes) {
        payload.attributes = attributes;
      }

      await postCart(
        endpoints.update,
        payload
      );
    }

    if (changes.additions.length > 0) {
      await postCart(
        endpoints.add,
        {
          items: changes.additions
        }
      );
    }
  }

  /**
   * Update main product and linked gifts.
   */
  async function updateBundle(
    itemKey,
    nextQuantity,
    variantId
  ) {
    return runExclusive(async () => {
      try {
        const cart = await readCart();

        const item = cart.items.find(
          (entry) => entry.key === itemKey
        );

        if (!item) {
          throw new Error(
            'Product not found in Cart'
          );
        }

        if (!isMain(item)) {
          throw new Error(
            'This item is not a bundle main product'
          );
        }

        const changes = calculateChanges(
          cart,
          {
            key: itemKey,
            quantity: toQuantity(nextQuantity)
          }
        );

        await commitChanges(changes);

        await refreshDrawer(false);

        if (
          typeof publish === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined'
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cartData: state.cart,
            variantId
          });
        }

        return state.cart;
      } catch (error) {
        console.error(
          '[TST Bundle Update]',
          error
        );

        try {
          await refreshDrawer(false);
        } catch (recoveryError) {
          console.warn(
            '[TST Bundle Recovery]',
            recoveryError
          );
        }

        throw error;
      }
    });
  }

  /**
   * Customer deliberately removes a specific gift.
   *
   * BOTH changes are sent together:
   *
   * 1. Gift line quantity = 0.
   * 2. Declined variant ID saved to Cart Attributes.
   *
   * Future reconciliation skips the declined variant.
   */
  async function removeGift(
    giftKey,
    bundleId,
    variantId
  ) {
    return runExclusive(async () => {
      const cart = await readCart();

      const gift = cart.items.find(
        (item) => item.key === giftKey
      );

      if (!gift || !isGift(gift)) {
        throw new Error(
          'Gift no longer exists in Cart'
        );
      }

      if (
        getBundleId(gift) !== bundleId ||
        String(gift.variant_id) !== String(variantId)
      ) {
        throw new Error(
          'Gift information does not match the Cart'
        );
      }

      const changes = {
        updates: {
          [gift.key]: 0
        },
        additions: []
      };

      const attributes = getDeclinedAttribute(
        cart,
        bundleId,
        variantId
      );

      /**
       * Persist suppression and remove line together.
       */
      await commitChanges(
        changes,
        attributes
      );

      await refreshDrawer(false);

      return state.cart;
    });
  }

  /**
   * Repair missing gifts and remove orphan gifts.
   *
   * Deliberately removed gifts remain excluded.
   */
  async function reconcile() {
    return runExclusive(async () => {
      const cart = await readCart();

      const changes = calculateChanges(cart);

      if (changes.changed) {
        await commitChanges(changes);

        await refreshDrawer(false);
      } else {
        state.cart = cart;
        lockCartPageGiftRows(cart);
      }

      return changes.changed;
    });
  }

  /**
   * Patch Dawn CartItems.updateQuantity.
   *
   * Native Dawn still handles ordinary products.
   * TST handles linked bundle main products.
   */
  function installCartIntegration() {
    if (state.installed) {
      return;
    }

    if (
      typeof CartItems === 'undefined' ||
      !CartItems.prototype.updateQuantity
    ) {
      return;
    }

    const original =
      CartItems.prototype.updateQuantity;

    CartItems.prototype.updateQuantity = function(
      line,
      nextQuantity,
      event,
      name,
      variantId
    ) {
      const index = Number(line);

      const currentItem =
        state.cart?.items?.[index - 1];

      if (currentItem && isGift(currentItem)) {
        return;
      }

      if (
        !currentItem ||
        !isMain(currentItem)
      ) {
        return original.call(
          this,
          line,
          nextQuantity,
          event,
          name,
          variantId
        );
      }

      const itemKey = currentItem.key;

      return updateBundle(
        itemKey,
        nextQuantity,
        variantId
      ).catch((error) => {
        console.error(
          '[TST Bundle Quantity]',
          error
        );

        const errorElement =
          document.getElementById('cart-errors') ||
          document.getElementById(
            'CartDrawer-CartErrors'
          );

        if (errorElement) {
          errorElement.textContent =
            error.message || 'Unable to update bundle.';
        }
      });
    };

    state.installed = true;
  }

  /**
   * Dedicated Gift trash button handler.
   *
   * Do not use Dawn cart-remove-button for gifts.
   */
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const button = target.closest(
        '[data-tst-remove-gift]'
      );

      if (!button) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (button.disabled) {
        return;
      }

      const giftKey = button.dataset.giftKey;
      const bundleId = button.dataset.giftBundleId;
      const variantId = button.dataset.giftVariantId;

      if (
        !giftKey ||
        !bundleId ||
        !variantId
      ) {
        return;
      }

      button.disabled = true;

      removeGift(
        giftKey,
        bundleId,
        variantId
      ).catch((error) => {
        console.error(
          '[TST Remove Gift]',
          error
        );

        button.disabled = false;

        const errorElement =
          document.getElementById(
            'CartDrawer-CartErrors'
          );

        if (errorElement) {
          errorElement.textContent =
            error.message || 'Unable to remove gift.';
        }
      });
    },
    true
  );

  /**
   * Block gift quantity editing on the Cart page.
   */
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const row = target.closest(
        '[id^="CartItem-"]'
      );

      if (
        !row ||
        row.dataset.tstLinkedGift !== 'true'
      ) {
        return;
      }

      const control = target.closest(
        'quantity-input, cart-remove-button'
      );

      if (control) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  /**
   * Prevent Checkout during mutation.
   */
  document.addEventListener(
    'click',
    (event) => {
      if (state.pending === 0) {
        return;
      }

      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      if (
        target.closest(
          '#CartDrawer-Checkout, #checkout, button[name="checkout"]'
        )
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  /**
   * Compatibility API for quantity-breaks.liquid.
   */
  window.TSTBundleCartGuard = {
    reconcile,
    refreshDrawer,
    removeGift,
    calculateGiftTargets,
    getCart: () => state.cart,
    getDeclinedGifts
  };

  async function initialize() {
    installCartIntegration();

    try {
      state.cart = await readCart();

      lockCartPageGiftRows(state.cart);

      await reconcile();
    } catch (error) {
      console.warn(
        '[TST Bundle Initialization]',
        error
      );
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    () => {
      installCartIntegration();

      reconcile().catch((error) => {
        console.warn(
          '[TST Bundle Section Reload]',
          error
        );
      });
    }
  );
})();
